/**
 * @description Componente para cadastrar novo tutor no fluxo veterinário
 * @module VetTutorForm
 * @note Baseado em CreatePatient.tsx
 */

import { Box, Stack, Typography } from "@mui/material";
import {
  FormTextInput,
  FormSelectInput,
  FormButtonGroup,
} from "../../../../components/form";
import { DisplayTextField } from "../../../../components/common";
import { useRegex } from "../../../../utils/useRegex";
import { useFormatter } from "../../../../utils/useFormatter";
import dayjs from "dayjs";

interface IProps {
  control: any;
  cpf: string;
  onGoBack: () => void;
  onSubmit: (data: any) => void | Promise<void>;
  serproResponse?: any; // Dados do Serpro (se disponível)
  loading?: boolean;
}

// Opções de gênero (mesmo padrão do CreatePatient)
const typeOptionsGender: Array<any> = [
  { display: "Masculino", value: "male" },
  { display: "Feminino", value: "female" },
];

/**
 * Componente para cadastrar novo tutor
 * Exibe formulário com os mesmos campos de CreatePatient.tsx
 * Se houver dados do Serpro, exibe campos preenchidos (somente leitura)
 * Caso contrário, permite entrada manual
 */
export function VetTutorForm({
  control,
  cpf,
  onGoBack,
  onSubmit,
  serproResponse,
  loading = false,
}: IProps) {
  // Se tem dados do Serpro, exibe campos preenchidos
  const hasSerproData = !!serproResponse;

  return (
    <Stack spacing={2}>
      {/* Título */}
      <Stack>
        <Typography variant="h6" fontWeight="600" color="neutral600.main">
          Cadastrar Tutor
        </Typography>
        <Typography variant="body1">
          {hasSerproData
            ? "Confirme as informações do tutor encontradas no Serpro"
            : "Preencha as informações do tutor para continuar"}
        </Typography>
      </Stack>

      {/* Campo: Nome completo */}
      <Box>
        {hasSerproData ? (
          <DisplayTextField
            variant="filled"
            label="Nome completo"
            value={serproResponse?.nome || ""}
          />
        ) : (
          <FormTextInput
            label="Nome completo"
            placeholder="Nome completo do tutor"
            name="tutorName"
            control={control}
            required={true}
            autoFocus
          />
        )}
      </Box>

      {/* Campo: CPF (somente leitura) */}
      <Box>
        <DisplayTextField
          variant="filled"
          label="CPF"
          value={useFormatter.formatCPF(cpf)}
        />
      </Box>

      {/* Campo: Data de nascimento (se disponível no Serpro) */}
      {hasSerproData && serproResponse?.nascimento && (
        <Box>
          <DisplayTextField
            variant="filled"
            label="Data de nascimento"
            value={
              dayjs(serproResponse.nascimento, "DDMMYYYY").isValid()
                ? dayjs(serproResponse.nascimento, "DDMMYYYY").format(
                    "DD/MM/YYYY"
                  )
                : ""
            }
          />
        </Box>
      )}

      {/* Campo: Contato/Telefone */}
      <Box>
        <FormTextInput
          label="Contato"
          placeholder="Número de telefone"
          name="tutorPhoneNumber"
          control={control}
          required={true}
          pattern={useRegex.phoneNumber}
          mask={useFormatter.formatPhoneNumber}
          maxLength={15}
        />
      </Box>

      {/* Campo: Sexo de nascimento */}
      <Box>
        <FormSelectInput
          name="tutorGender"
          control={control}
          label="Sexo de nascimento"
          selectOptions={typeOptionsGender}
          required
        />
      </Box>

      {/* Botões de ação */}
      <FormButtonGroup
        goBackButtonText="Voltar"
        onGoBackButtonClick={onGoBack}
        nextButtonText="Cadastrar Tutor"
        onNextButtonClick={() => {
          // Coleta os dados do formulário manualmente
          const formData = {
            tutorName: hasSerproData ? serproResponse?.nome : undefined,
            tutorPhoneNumber: undefined, // Será preenchido pelo usuário
            tutorGender: undefined, // Será preenchido pelo usuário
          };
          onSubmit(formData);
        }}
        loading={loading}
        disabled={false}
      />
    </Stack>
  );
}
